import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Clock, PencilRuler, TriangleAlert } from "lucide-react";
import {  useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import type { Level } from "types";
import { cardCtaVisibilityAssertion, isPAthOrCourseAssertion, showProgressBarAssertion } from "./assertion";
import {
  DarkFill,
  HeroBannerContainer,
  HeroBannerInfo,
  HeroBannerMask,
  MaskWrapper,
  StatusBoxesStyle,
  TitleTypography
} from "./HeroBanner.styles";
import { HeroBannerProps } from "./HeroBanner.types";
import { handleWidth, thereAreVirtualClassroom } from "./utils";
import {
  CardSignature,
  VideoCover
} from "../../components";
import { BoxCollapse, StyledDurationBadge } from "../../components/CarouselCardRestyle/CarouselCard.style";
import { HiddenCTA } from "../../components/CarouselCardRestyle/HiddenCTA.component";
import { StatusBoxes } from "../../components/CarouselCardRestyle/StatusBoxes.component";
import { TopicTags } from "../../components/CarouselCardRestyle/TopicTags.component";
import { CtaOverlay } from "../../components/CtaOverlay/CtaOverlay.component";
import { ProgressBar } from "../../components/ProgressBar/ProgressBar.component";
import {
  LEVEL, TAB_ITEMS
} from "../../consts";
import { useElementHeight, usePointerType, useTimer, useType } from "../../hooks";
import { useNavbarStore } from "../../stores";
import { formatDuration_h_m, hasPassedExpiration } from "../../utils/datetime";
import { getPreviousRoute } from "../../utils/general";
import { NavigationBreadcrumbs } from "../NavigationBreadcrumbs";
import { TruncatedText } from "../TruncatedText";

export function HeroBanner({
  badge,
  booked,
  categories,
  coverPublicURL,
  coverVideoPublicUrl,
  courseId=undefined,
  duration,
  ecmRegistration,
  ecmType,
  editionNumber,
  editionVirtualLink,
  enrollType,
  expirationDate,
  id,
  ecm_specialization,
  editionsLength,
  isEnable,
  isFavourite,
  isMandatory,
  isStandAlone,
  isSurvey,
  isTest,
  isToj,
  learningObjectType,
  learningObjectTypology,
  meetingDetails,
  percentageOfCompletion,
  rating,
  rootId=undefined,
  setOpenTojModal,
  shortDescription,
  startDate,
  endDate,
  setOpenModalEcm,
  status,
  title,
  isCurrentSession,
  completable,
  canAccessNow,
  credits,
  isLinkRelative,
  link,
  attributes,
  attributesIds,
  certificates,
  learningActivities,
  ...props
}: HeroBannerProps) {

  const theme = useTheme();
  const { t } = useTranslation();
  const {  pathname } = useLocation();
  const { hasMouse } = usePointerType();

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isXLarge = useMediaQuery(theme.breakpoints.up("max"));

  const [videoVisible, setVideoVisible] = useState(Boolean(coverVideoPublicUrl));

  const { stopTimer, startTimer, isActive, setIsActive } = useTimer(3000);
  const elementWidth = handleWidth(isTablet, isLarge, isXLarge, isActive);

  const navbarItem = useNavbarStore(state => state.item);
  const previousPath = getPreviousRoute({
    courseId,
    id,
    learningObjectTypology,
    path: pathname,
    rootId,
    section: navbarItem
  });
  const typeObjectLearning = useType(learningObjectType, learningObjectTypology);

  const isPAthOrCourse = isPAthOrCourseAssertion(learningObjectTypology);
  const durationNumber = parseInt(`${duration}`);

  const showProgressBar = showProgressBarAssertion(percentageOfCompletion, status,learningObjectTypology);

  const cardCtaVisibility = cardCtaVisibilityAssertion(status, learningObjectTypology, isTest);
  const gradientHookHeight = useElementHeight("gradient-hook");

  useEffect(() => {
    if(isTablet){
      setIsActive(true);
    }
  }, [isTablet]);

  useEffect(() => {
    if(!hasMouse){ //avoid disabling gradient when user has not mouse or trackpad
      setIsActive(true);
      stopTimer();
    }
  }, []);

  function typeActualObject(LOtype: Level) {
    if(LOtype !== "PATH" && LOtype !== "COURSE"){
      return t("learning_activity");
    }
    if(LOtype === "PATH") return t("package");
    if(LOtype === "COURSE") return t("course");
  }

  const typeLo = typeActualObject(learningObjectTypology) ?? "";

  let ariaLabelOnCard = `${title}`;
  if(typeLo){
    ariaLabelOnCard = `${t("typology_carousel")}:${typeLo}.${title}`;
  }

  return (
    <HeroBannerContainer
      id={ `hero-banner-${id}` }
      data-testid="hero-banner-root"
      role="main"
      tabIndex={ 0 }
      aria-label={ ariaLabelOnCard }
      $coverPublicURL={ coverPublicURL }
      onMouseEnter={ stopTimer }
      onMouseLeave={ () => {
        hasMouse && startTimer(); //avoid disabling gradient when user has not mouse or trackpad
      } }
      $isActive={ isActive }
      $isMobile={ isMobile }
      $isTablet={ isTablet }
      $isMedium={ isMedium }
      $isLarge={ isLarge }
      $isXLarge={ isXLarge }
      $elementWidth={ elementWidth }
    >
      <MaskWrapper
        id="herobanner-mask"
        $isActive={ isActive }
        $isTablet={ isTablet }
        $isLarge={ isLarge }
        $isXLarge={ isXLarge }
      >
        <DarkFill
          id="gradient-darkfill"
          $isActive={ isActive }
          $isTablet={ isTablet }
          $gradientHookHeight={ gradientHookHeight }
        />
        <HeroBannerMask
          id="gradient-mask"
          $isActive={ isActive }
          $isTablet={ isTablet }
          $gradientHookHeight={ gradientHookHeight }
        />
      </MaskWrapper>

      <NavigationBreadcrumbs learningObjectTypology={ learningObjectTypology } previousPath={ previousPath }/>
      {
        videoVisible && coverVideoPublicUrl ?
          <VideoCover
            onEnded={ ()=> { setVideoVisible(false)} }
            url={ coverVideoPublicUrl }
          /> : null
      }

      <HeroBannerInfo
        id="hero-banner-info"
        data-testid="hero-banner-info"
        aria-label={ t("hero_banner_info_aria_label") }
        component="section"
        className="herobanner-info"
        position={ "relative" }
        $isTablet={ isTablet }
        { ...props }
      >
        <div id="gradient-hook"
          style={ {
            width: "100%"
          } }
        >
          <StatusBoxesStyle
            className="herobanner-badges"
            aria-hidden="true"
            tabIndex={ -1 }
            $isTablet={ isTablet }
          >
            <Stack sx={ {
              display:"flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "0.5rem"
            } }
            >
              { duration && !isToj && !isNaN(durationNumber) && durationNumber > 0 ?
                <StyledDurationBadge
                  tabIndex={ -1 }
                  width="fit-content"
                  boxSizing="content-box"
                  border={ `1px solid ${theme.customColors.textTag}` }
                  sx={ {
                    alignItems: "center",
                    backgroundColor: theme.customColors.backgroundSecondary,
                    borderRadius: "0.25rem",
                    gap:"0.25rem",
                    display: "flex",
                    height: "1.5625rem",
                    justifyContent: "center",
                    padding: "0.10938rem 0.4375rem"
                  } }
                  padding={ "0.109rem 0.437rem" }
                  gap={ "0.1875rem" }
                  $completed={ status === "C" }
                >
                  <Clock size={ "0.875rem" } color={ theme.customColors.textTag } />
                  <span style={ {
                    lineHeight: "normal",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: theme.customColors.textTag
                  } }>
                    { duration && formatDuration_h_m(duration) }
                  </span>
                </StyledDurationBadge> : null
              }
              <StatusBoxes
                id={ id }
                isMandatory={ isMandatory }
                isBooked={ booked && (
                  learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS
              || learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS
              || isToj
                ) }
                endDate={ endDate }
                isCompleted={ status === "C" }
                isExpired={ hasPassedExpiration(expirationDate) }
                expirationDate={ expirationDate }
                startDate={ startDate }
                learningObjectTypology={ learningObjectTypology }
                isFavourite={ isFavourite }
                attributes={ attributes }
                credits={ credits }
                usedInsideBigBanner={ true }
              />
            </Stack>
            { status === "W" && (
              <Box
                id={ `hero-banner-${id}-status-warning` }
                data-testid="hero-banner-status-warning"
                aria-label={ t("hero_banner_status_warning_aria_label") }
                tabIndex={ -1 }
                sx={ {
                  display: "flex",
                  left: "2.5rem",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.2rem 0.4375rem",
                  gap: "0.25rem",
                  border: `1px solid ${theme.customColors.backgroundAccent}`,
                  backgroundColor: theme.customColors.backgroundSecondaryAlternative,
                  borderRadius: "0.25rem",
                  width: "fit-content"
                } }
              >
                <TriangleAlert size="0.875rem" color={ theme.customColors.textTag }/>
                <Typography tabIndex={ -1 } id={ `${id}-card-statusBox` } sx={ { fontSize: "0.75rem", fontWeight: "600" } }
                  color={ theme.customColors.textTag }>
                  { t("validation_text") }
                </Typography>
              </Box>
            ) }
            { learningActivities && thereAreVirtualClassroom(learningActivities) && (
              <Box
                id={ `hero-banner-${id}-virtual-classroom-warning` }
                data-testid="hero-banner-virtual-classroom-warning"
                aria-label={ t("virtual_classroom_badge_label") }
                tabIndex={ -1 }
                sx={ {
                  display: "flex",
                  left: "2.5rem",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.2rem 0.4375rem",
                  gap: "0.25rem",
                  border: `1px solid ${theme.customColors.textTag}`,
                  backgroundColor: theme.customColors.backgroundSecondary,
                  borderRadius: "0.25rem",
                  width: "fit-content"
                } }
              >
                <PencilRuler size="0.875rem" color={ theme.customColors.textTag }/>
                <Typography tabIndex={ -1 } id={ `${id}-card-virtual-classroom-presence` } sx={ { fontSize: "0.75rem", fontWeight: "600" } }
                  color={ theme.customColors.textTag }>
                  {  t("virtual_classroom_badge_label") }
                </Typography>
              </Box>
            ) }
            { isPAthOrCourse && !completable && (
              <Box
                id={ `hero-banner-${id}-not-completable` }
                data-testid="hero-banner-not-completable"
                aria-label={ t("hero_banner_not_completable_aria_label") }
                key={ t("completable-text") }
                tabIndex={ -1 }
                sx={ {
                  marginBottom: "1rem",
                  left: "2.5rem",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.2rem 0.4375rem",
                  gap: "0.25rem",
                  border: `1px solid ${theme.customColors.borderError}`,
                  backgroundColor: theme.customColors.backgroundWarning,
                  borderRadius: "0.25rem",
                  width: isMobile ? "calc(100% - 3rem)" : "fit-content"
                } }
              >
                <Typography tabIndex={ -1 } id={ `${id}-card-statusBox` } sx={ { fontSize: "0.75rem", fontWeight: "600" } }
                  color={ theme.customColors.textTag }>
                  { t("completable-text") }
                </Typography>
              </Box>
            ) }
          </StatusBoxesStyle>
          {
            isTest ?
              <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
                {   t("test") }
              </span>
              :
              isSurvey ?
                <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
                  {   t("survey") }
                </span> :
                null
          }
          { /* if is tablet, takes all the space, othrwise when active is 40%, when disabled 35% */ }
          <Stack
            width={ elementWidth }
            gap={ "0.5rem" }
            marginBottom={ showProgressBar ? "0.5rem" : "1rem" }
            id="title-herobanner-stack"
          >
            <Stack
              sx={ {
                opacity: isActive || isTablet ? 1 : 0,
                transition: "opacity 0.3s ease-out"
              } }
            >
              {
                ecm_specialization && ecmType ?
                  <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
                    { ecmType.toUpperCase() }
                  </span> :
                  typeObjectLearning && !isTest && !isSurvey  ?
                    <span className={ "type" } style={ { color: theme.customColors.textPrimary } }>
                      { typeObjectLearning }
                    </span>
                    : null
              }
            </Stack>
            <TruncatedText
              text={ title }
              maxLength={ 200 }
              webkitLineClamp={ 3 }
            >
              <TitleTypography >
                { title }
              </TitleTypography>
            </TruncatedText>


          </Stack>
          { cardCtaVisibility.visibility ? (
            <BoxCollapse
              className={ "box_collapse" }
              sx={ {
                pointerEvents: "none",
                gap: "0.25rem",
                width: elementWidth
              } }
            >
              <CardSignature
                ecm_specialization={ ecm_specialization }
                editionNumber={ editionNumber }
                expirationDate={ expirationDate }
                startDate={ startDate }
                endDate={ endDate }
                gap={ 2 }
                isToj={ isToj }
                learningObjectTypology={ learningObjectTypology }
                meetingDetails={ meetingDetails }
                canAccessNow={ canAccessNow }
              />
              {
                showProgressBar && (
                  <ProgressBar
                    progression={ percentageOfCompletion }
                    maxWidth={ isTablet ? elementWidth : "100%" }
                  />
                )
              }
              { categories && (categories.length > 0) &&
                <TopicTags
                  topicTags={ categories }
                  tagsCollapsed={ true }
                />
              }

              <HiddenCTA
                badge={ badge }
                disabled={ !isEnable ||
                (cardCtaVisibility.disabled ||
                  status === "R") ||
                  (learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS &&
                    (!editionsLength || (status === "E" && !isCurrentSession && !editionVirtualLink)))
                }
                ecmRegistration={ ecmRegistration }
                editionsLength={ editionsLength }
                enrollType={ enrollType }
                className={ "card-cta" }
                courseId={ courseId }
                id={ id }
                ecm_specialization={ ecm_specialization }
                maxWidth={ "600px" }
                isStandAlone={ isStandAlone }
                learningObjectTypology={ learningObjectTypology }
                learningObjectType={ learningObjectType }
                linkVirtualClass={ editionVirtualLink }
                percentageOfCompletion={ percentageOfCompletion }
                isTest={ isTest }
                isSurvey={ isSurvey }
                rootId={ rootId }
                simple={ false }
                status={ status }
                triggerOpenTabs={ openEditions }
                isCard={ false }
                setOpenRegistrationModal={ setOpenModalEcm }
                setOpenTojModal={ setOpenTojModal }
                canAccessNow={ canAccessNow }
                isLinkRelative={ isLinkRelative }
                isHeroBanner={ true }
                link={ link }
                attributesIds={ attributesIds }
                certificates={ certificates }
                isFavourite={ isFavourite }
                ctaPosition="start"
              />
            </BoxCollapse>  ) :  null
          }
        </div>

        <CtaOverlay
          data={ {
            isStandAlone: isStandAlone,
            isFavourite: isFavourite,
            id: id,
            learningObjectTypology: learningObjectTypology,
            rating: rating,
            courseId: courseId,
            rootId: rootId
          } }
          certificates={ certificates }
          badge={ badge }
          downloadLabel={ t("certificate") }
        />
      </HeroBannerInfo>

    </HeroBannerContainer>
  );

  function openEditions() {
    const tabsComponent = document.querySelector(".MuiTabs-flexContainer") as HTMLDivElement;

    if (tabsComponent) {
      const tabs = tabsComponent.querySelectorAll("button") as NodeListOf<HTMLButtonElement>;
      const labelToCheck =
        learningObjectTypology === LEVEL.COURSE ? TAB_ITEMS.ACTIVITIES
          : learningObjectTypology === LEVEL.PATH ? "modules"
            : TAB_ITEMS.EDITIONS;
      if (labelToCheck !== "modules"){
        tabs.forEach((tab) => {
          if (tab.id.includes(labelToCheck)) {
            tab.click();
            tab.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
          }
        });
      }
      else {
        const modules = document.querySelector(".modules-tab");
        if(modules){
          modules.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
      }
    }
  }
}

